import { useCallback, useEffect, useState, type FormEvent } from "react";
import { Link, useParams } from "react-router-dom";
import {
  listAccounts,
  listLicenses,
  createLicense,
  updateLicense,
  suspendLicense,
  reactivateLicense,
  reassignMachine,
  resetAccountPassword,
  LICENSE_PRESETS,
  presetFromFeatures,
  type AccountDoc,
  type LicenseDoc,
  fsTimestampToDate,
  formatExpiry,
} from "../api/functions";

export function AccountDetailPage() {
  const { accountId = "" } = useParams();
  const [account, setAccount] = useState<AccountDoc | null>(null);
  const [licenses, setLicenses] = useState<LicenseDoc[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [showCreate, setShowCreate] = useState(false);
  const [showResetPwd, setShowResetPwd] = useState(false);

  const refresh = useCallback(async () => {
    try {
      const [accs, lics] = await Promise.all([listAccounts(), listLicenses({ accountId })]);
      setAccount(accs.accounts.find(a => a.accountId === accountId) ?? null);
      setLicenses(lics.licenses);
    } catch (err) {
      setError((err as { message?: string }).message ?? "Failed to load");
    }
  }, [accountId]);

  useEffect(() => { refresh(); }, [refresh]);

  if (error) return <div className="text-sm text-red-600">{error}</div>;
  if (!account || !licenses) return <div className="text-slate-500">Loading…</div>;

  return (
    <div className="space-y-6">
      <div className="text-sm text-slate-500">
        <Link to="/admin" className="hover:underline">← Back to accounts</Link>
      </div>

      <header className="flex items-baseline justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-slate-800">{account.name}</h1>
          <p className="text-slate-500 text-sm">{account.email}</p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => setShowResetPwd(s => !s)}
            className="bg-slate-200 hover:bg-slate-300 text-slate-800 text-sm font-medium rounded-md px-4 py-2"
          >
            {showResetPwd ? "Cancel" : "Reset password"}
          </button>
          <button
            onClick={() => setShowCreate(s => !s)}
            className="bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-medium rounded-md px-4 py-2"
          >
            {showCreate ? "Cancel" : "+ New license"}
          </button>
        </div>
      </header>

      {showResetPwd && (
        <ResetPasswordForm
          accountId={accountId}
          email={account.email}
          onDone={() => setShowResetPwd(false)}
        />
      )}

      {showCreate && (
        <CreateLicenseForm
          accountId={accountId}
          onCreated={() => { setShowCreate(false); refresh(); }}
        />
      )}

      {licenses.length === 0 ? (
        <div className="text-slate-500 text-sm">No licenses yet for this account.</div>
      ) : (
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-slate-100 text-slate-600 text-left">
              <tr>
                <th className="px-4 py-3 font-medium">Key</th>
                <th className="px-4 py-3 font-medium">Restaurant ID</th>
                <th className="px-4 py-3 font-medium">Restaurant</th>
                <th className="px-4 py-3 font-medium">Status</th>
                <th className="px-4 py-3 font-medium">Plan</th>
                <th className="px-4 py-3 font-medium">Created</th>
                <th className="px-4 py-3 font-medium">Expiry</th>
                <th className="px-4 py-3 font-medium">Machine</th>
                <th className="px-4 py-3 font-medium">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {licenses.map(l => (
                <LicenseRow key={l.docId} license={l} onChange={refresh} />
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function ResetPasswordForm({
  accountId, email, onDone,
}: { accountId: string; email: string; onDone: () => void }) {
  const [newPassword, setNewPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (newPassword.length < 8) {
      setError("Password must be at least 8 characters.");
      return;
    }
    if (!confirm(`Set a new password for ${email}?\n\nThe customer's existing password will stop working immediately.`)) return;
    setBusy(true);
    setError(null);
    try {
      await resetAccountPassword({ accountId, newPassword });
      alert(`Password updated for ${email}.\n\nShare the new password with the customer over a secure channel.`);
      setNewPassword("");
      onDone();
    } catch (err) {
      setError((err as { message?: string }).message ?? "Failed to reset password");
      setBusy(false);
    }
  };

  return (
    <form onSubmit={onSubmit} className="bg-white rounded-lg shadow p-6 space-y-4 max-w-xl">
      <h2 className="font-semibold text-slate-800">Reset password</h2>
      <p className="text-xs text-slate-500">
        Type a new password for <span className="font-mono">{email}</span> and share it with the customer.
        They can change it themselves later from the customer portal.
      </p>
      <label className="block">
        <span className="text-sm text-slate-700">New password (min 8 chars)</span>
        <input
          type="text"
          value={newPassword}
          onChange={e => setNewPassword(e.target.value)}
          required
          autoComplete="new-password"
          className="mt-1 block w-full rounded-md border-slate-300 border px-3 py-2 text-sm font-mono"
        />
      </label>
      {error && <div className="text-sm text-red-600">{error}</div>}
      <button
        type="submit"
        disabled={busy}
        className="bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 text-white font-medium rounded-md px-5 py-2 text-sm"
      >
        {busy ? "Updating…" : "Set new password"}
      </button>
    </form>
  );
}

function CreateLicenseForm({ accountId, onCreated }: { accountId: string; onCreated: () => void }) {
  const [restaurantName, setRestaurantName] = useState("");
  const [restaurantId, setRestaurantId] = useState("");
  const [expiryISO, setExpiryISO] = useState(defaultExpiry());
  const [notes, setNotes] = useState("");
  // Default to the most-inclusive preset (POS+Captain) so admin doesn't accidentally
  // create a downgraded license on the no-pick path.
  const [planId, setPlanId] = useState<string>(LICENSE_PRESETS[LICENSE_PRESETS.length - 1].id);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    const preset = LICENSE_PRESETS.find(p => p.id === planId);
    if (!preset) {
      setError("Pick a plan.");
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const r = await createLicense({
        accountId,
        restaurantName: restaurantName.trim(),
        restaurantId:   restaurantId.trim() || undefined,
        expiryISO: new Date(expiryISO).toISOString(),
        notes: notes.trim() || undefined,
        features: preset.features,
      });
      alert(`License created.\n\nKey: ${r.licenseKey}\nRestaurant ID: ${r.restaurantId}\n\nCopy from the table.`);
      onCreated();
    } catch (err) {
      setError((err as { message?: string }).message ?? "Failed to create license");
      setBusy(false);
    }
  };

  return (
    <form onSubmit={onSubmit} className="bg-white rounded-lg shadow p-6 space-y-4">
      <h2 className="font-semibold text-slate-800">New license</h2>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <label className="block">
          <span className="text-sm text-slate-700">Restaurant / outlet name</span>
          <input
            value={restaurantName}
            onChange={e => setRestaurantName(e.target.value)}
            required
            className="mt-1 block w-full rounded-md border-slate-300 border px-3 py-2 text-sm"
          />
        </label>
        <label className="block">
          <span className="text-sm text-slate-700">Restaurant ID <span className="text-slate-400">(optional)</span></span>
          <input
            value={restaurantId}
            onChange={e => setRestaurantId(e.target.value.toUpperCase())}
            placeholder="leave blank to auto-generate"
            className="mt-1 block w-full rounded-md border-slate-300 border px-3 py-2 text-sm font-mono uppercase"
          />
        </label>
        <label className="block">
          <span className="text-sm text-slate-700">Expiry date</span>
          <input
            type="date"
            value={expiryISO}
            onChange={e => setExpiryISO(e.target.value)}
            required
            className="mt-1 block w-full rounded-md border-slate-300 border px-3 py-2 text-sm"
          />
        </label>
      </div>
      <label className="block">
        <span className="text-sm text-slate-700">Notes (admin only, optional)</span>
        <input
          value={notes}
          onChange={e => setNotes(e.target.value)}
          className="mt-1 block w-full rounded-md border-slate-300 border px-3 py-2 text-sm"
        />
      </label>
      <fieldset className="block">
        <legend className="text-sm text-slate-700">Plan</legend>
        <div className="mt-1 flex flex-wrap gap-4">
          {LICENSE_PRESETS.map(p => (
            <label key={p.id} className="inline-flex items-center gap-1.5 text-sm text-slate-700">
              <input
                type="radio"
                name="plan"
                value={p.id}
                checked={planId === p.id}
                onChange={() => setPlanId(p.id)}
                className="border-slate-300"
              />
              {p.label}
            </label>
          ))}
        </div>
      </fieldset>
      {error && <div className="text-sm text-red-600">{error}</div>}
      <button
        type="submit"
        disabled={busy}
        className="bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 text-white font-medium rounded-md px-5 py-2"
      >
        {busy ? "Creating…" : "Create license"}
      </button>
    </form>
  );
}

function LicenseRow({ license, onChange }: { license: LicenseDoc; onChange: () => void }) {
  const [busy, setBusy] = useState(false);
  const [editing, setEditing] = useState(false);
  const docId = license.docId!;

  const wrap = async (fn: () => Promise<unknown>) => {
    setBusy(true);
    try { await fn(); onChange(); }
    catch (err) { alert((err as { message?: string }).message ?? "Failed"); }
    finally { setBusy(false); }
  };

  const onSuspend = () => {
    if (!confirm("Suspend this license? The desktop will lock at the next heartbeat.")) return;
    wrap(() => suspendLicense({ licenseDocId: docId }));
  };

  const onReactivate = () => wrap(() => reactivateLicense({ licenseDocId: docId }));

  const onReassign = () => {
    if (!confirm("Clear the bound machine? The customer can then activate on a new PC.")) return;
    wrap(() => reassignMachine({ licenseDocId: docId }));
  };

  const onCopy = () => {
    navigator.clipboard.writeText(license.licenseKey);
  };

  const onCopyId = () => {
    if (license.restaurantId) navigator.clipboard.writeText(license.restaurantId);
  };

  return (
    <>
      <tr className="hover:bg-slate-50">
        <td className="px-4 py-3 font-mono text-xs">
          <button onClick={onCopy} className="text-indigo-600 hover:underline" title="Copy">
            {license.licenseKey}
          </button>
        </td>
        <td className="px-4 py-3 font-mono text-xs">
          {license.restaurantId ? (
            <button onClick={onCopyId} className="text-slate-700 hover:underline" title="Copy">
              {license.restaurantId}
            </button>
          ) : <span className="text-slate-400">—</span>}
        </td>
        <td className="px-4 py-3 text-slate-700">{license.restaurantName}</td>
        <td className="px-4 py-3"><StatusPill status={license.status} /></td>
        <td className="px-4 py-3 text-slate-700 text-xs">
          {LICENSE_PRESETS.find(p => p.id === presetFromFeatures(license.features))?.label ?? "—"}
        </td>
        <td className="px-4 py-3 text-slate-500">{formatExpiry(license.createdAt)}</td>
        <td className="px-4 py-3 text-slate-700">{formatExpiry(license.expiry)}</td>
        <td className="px-4 py-3 text-slate-500 text-xs">
          {license.machineId
            ? <>{license.machineName ?? "—"}<br /><span className="font-mono">{license.machineId}</span></>
            : "Not activated"}
        </td>
        <td className="px-4 py-3 space-x-2 whitespace-nowrap">
          <button disabled={busy} onClick={() => setEditing(true)} className="text-xs text-indigo-600 hover:underline">Edit</button>
          {license.status === "active"
            ? <button disabled={busy} onClick={onSuspend}    className="text-xs text-amber-700 hover:underline">Suspend</button>
            : <button disabled={busy} onClick={onReactivate} className="text-xs text-emerald-700 hover:underline">Reactivate</button>}
          {license.machineId && (
            <>
              <button disabled={busy} onClick={onReassign} className="text-xs text-slate-700 hover:underline">Reassign</button>
              <a
                href={`/reports/${docId}`}
                target="_blank"
                rel="noreferrer"
                className="text-xs text-slate-700 hover:underline"
              >
                Dashboard ↗
              </a>
            </>
          )}
        </td>
      </tr>
      {editing && (
        <EditLicenseModal
          license={license}
          onClose={() => setEditing(false)}
          onSaved={() => { setEditing(false); onChange(); }}
        />
      )}
    </>
  );
}

function EditLicenseModal({
  license, onClose, onSaved,
}: { license: LicenseDoc; onClose: () => void; onSaved: () => void }) {
  const docId = license.docId!;
  const [restaurantName, setRestaurantName] = useState(license.restaurantName);
  const [restaurantId, setRestaurantId]     = useState(license.restaurantId);
  const initialExpiry = fsTimestampToDate(license.expiry);
  const [expiryISO, setExpiryISO] = useState(
    initialExpiry ? initialExpiry.toISOString().slice(0, 10) : "",
  );
  // Map current features to one of the radio presets. Legacy licenses (missing
  // features) resolve to POS+Captain, matching the server-side default.
  const initialPlanId = presetFromFeatures(license.features);
  const [planId, setPlanId] = useState<string>(initialPlanId);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const onSave = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);

    const preset = LICENSE_PRESETS.find(p => p.id === planId);
    if (!preset) {
      setError("Pick a plan.");
      return;
    }

    const update: {
      licenseDocId: string;
      restaurantName?: string;
      restaurantId?: string;
      expiryISO?: string;
      features?: string[];
    } = { licenseDocId: docId };

    if (restaurantName.trim() !== license.restaurantName) update.restaurantName = restaurantName.trim();
    if (restaurantId.trim().toUpperCase() !== license.restaurantId) update.restaurantId = restaurantId.trim();
    const newExpiryDate = expiryISO ? new Date(expiryISO) : null;
    const oldExpiryDate = initialExpiry;
    const expiryChanged = newExpiryDate && oldExpiryDate
      && newExpiryDate.toISOString().slice(0, 10) !== oldExpiryDate.toISOString().slice(0, 10);
    if (expiryChanged && newExpiryDate) update.expiryISO = newExpiryDate.toISOString();
    if (planId !== initialPlanId) update.features = preset.features;

    if (Object.keys(update).length === 1) {
      setError("No changes to save.");
      return;
    }

    setBusy(true);
    try {
      await updateLicense(update);
      onSaved();
    } catch (err) {
      setError((err as { message?: string }).message ?? "Failed to update");
      setBusy(false);
    }
  };

  return (
    <tr>
      <td colSpan={9} className="bg-slate-50 px-4 py-4">
        <form onSubmit={onSave} className="bg-white border border-slate-200 rounded-lg p-5 space-y-4 max-w-2xl">
          <div className="flex items-center justify-between">
            <h3 className="font-semibold text-slate-800">Edit license</h3>
            <span className="text-xs font-mono text-slate-500">{license.licenseKey}</span>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <label className="block">
              <span className="text-sm text-slate-700">Restaurant name</span>
              <input
                value={restaurantName}
                onChange={e => setRestaurantName(e.target.value)}
                required
                className="mt-1 block w-full rounded-md border-slate-300 border px-3 py-2 text-sm"
              />
            </label>
            <label className="block">
              <span className="text-sm text-slate-700">Restaurant ID</span>
              <input
                value={restaurantId}
                onChange={e => setRestaurantId(e.target.value.toUpperCase())}
                required
                className="mt-1 block w-full rounded-md border-slate-300 border px-3 py-2 text-sm font-mono uppercase"
              />
            </label>
            <label className="block">
              <span className="text-sm text-slate-700">Expiry date</span>
              <input
                type="date"
                value={expiryISO}
                onChange={e => setExpiryISO(e.target.value)}
                required
                className="mt-1 block w-full rounded-md border-slate-300 border px-3 py-2 text-sm"
              />
            </label>
          </div>
          <fieldset className="block">
            <legend className="text-sm text-slate-700">Plan</legend>
            <div className="mt-1 flex flex-wrap gap-4">
              {LICENSE_PRESETS.map(p => (
                <label key={p.id} className="inline-flex items-center gap-1.5 text-sm text-slate-700">
                  <input
                    type="radio"
                    name={`plan-${docId}`}
                    value={p.id}
                    checked={planId === p.id}
                    onChange={() => setPlanId(p.id)}
                    className="border-slate-300"
                  />
                  {p.label}
                </label>
              ))}
            </div>
          </fieldset>
          {error && <div className="text-sm text-red-600">{error}</div>}
          <div className="flex gap-2">
            <button
              type="submit"
              disabled={busy}
              className="bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 text-white font-medium rounded-md px-5 py-2 text-sm"
            >
              {busy ? "Saving…" : "Save"}
            </button>
            <button
              type="button"
              onClick={onClose}
              disabled={busy}
              className="bg-slate-200 hover:bg-slate-300 text-slate-800 font-medium rounded-md px-5 py-2 text-sm"
            >
              Cancel
            </button>
          </div>
          <p className="text-xs text-slate-500">
            Changes apply on the next heartbeat — already-cached envelopes on activated PCs continue to verify until they refresh.
          </p>
        </form>
      </td>
    </tr>
  );
}

function StatusPill({ status }: { status: string }) {
  const styles =
    status === "active"    ? "bg-emerald-100 text-emerald-800" :
    status === "suspended" ? "bg-amber-100 text-amber-800" :
                             "bg-slate-200 text-slate-700";
  return (
    <span className={"inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium " + styles}>
      {status}
    </span>
  );
}

function defaultExpiry(): string {
  const d = new Date();
  d.setFullYear(d.getFullYear() + 1);
  return d.toISOString().slice(0, 10);
}
